'use client'

import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Trash, Upload, X } from 'lucide-react'
import { useField, useForm, useFormFields, useTheme, useTranslation } from '@payloadcms/ui'
import Image from 'next/image'
import { Media } from '@/payload-types'

import './index.scss'

export const customPhotoUpload = ({
  path = 'images',
  maxFiles = 10,
  maxFileSize = 5 * 1024 * 1024,
}) => {
  const { showError, errorMessage, rows } = useField({ path, hasRows: true })
  const [images, setImages] = useState<Media[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef(null)
  const form = useForm()
  const [isUploading, setIsUploading] = useState(false)

  /**
   * Get images values from form fields
   */
  const imageRefs = useFormFields(([fields]) =>
    rows?.map((row, index) => ({
      id: row.id,
      image: fields[`${path}.${index}.image`]?.value || '',
    })),
  )

  const imageIds = imageRefs?.map((r) => r.image).filter(Boolean) || []

  /**
   * Fetch actual image docs when IDs change
   */
  useEffect(() => {
    if (imageIds.length === 0) return

    const fetchImages = async () => {
      const results = await Promise.all(
        imageIds.map(async (id) => {
          const res = await fetch(`/api/media/${id}`)
          if (!res.ok) return null
          return res.json()
        }),
      )
      setImages(results.filter(Boolean))
    }

    fetchImages()
  }, [imageIds.join(',')])

  // Handle adding new images
  const processFiles = async (files: FileList) => {
    setIsUploading(true)
    // start counting from however many rows exist already
    let rowCounter = rows?.length || 0

    for (const file of Array.from(files)) {
      try {
        // Upload file to Payload's media endpoint
        const formData = new FormData()
        formData.append('file', file)

        const res = await fetch('/api/media', {
          method: 'POST',
          body: formData,
        })

        if (res.ok) {
          const doc = await res.json()

          // 2. Add a new row with an empty image field
          form.addFieldRow({
            path,
            schemaPath: path,
            subFieldState: {
              image: {
                value: null,
                valid: true,
              },
            },
          })

          // Save the current row index for this file
          const currentIndex = rowCounter
          rowCounter++

          // 3. After Payload updates the form, assign the uploaded media ID
          requestAnimationFrame(() => {
            form.dispatchFields({
              type: 'UPDATE',
              path: `${path}.${currentIndex}.image`,
              value: doc.doc.id,
            })
            form.setModified(true)
          })
        }
      } catch (err) {
        console.error('Upload failed:', err)
      }
    }

    setIsUploading(false)
  }

  const handleDragOver = useCallback((e: any) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: any) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: any) => {
      e.preventDefault()
      setIsDragOver(false)

      const files = e.dataTransfer.files
      if (files.length > 0) {
        processFiles(files)
      }
    },
    [processFiles],
  )

  const handleFileSelect = useCallback(
    (e: any) => {
      const files = e.target.files
      if (files.length > 0) {
        processFiles(files)
      }
      // Reset input value to allow selecting the same files again
      e.target.value = ''
    },
    [processFiles],
  )

  const [isRemoving, setIsRemoving] = useState(false)

  const removeImage = useCallback(
    (id: number | string) => {
      if (!imageRefs || imageRefs.length === 0 || isRemoving) return

      setIsRemoving(true)

      // Find row index in form fields
      const index = imageRefs.findIndex((r) => r.image === id)
      // Image not found, already removed
      if (index === -1) {
        setIsRemoving(false)
        return
      }
      // 1. Remove from form fields first
      form.removeFieldRow({
        path,
        rowIndex: index,
      })
      form.setModified(true)

      // 2. Update local images state
      setImages((prev) => prev.filter((img) => img.id !== id))

      // Small delay to prevent rapid multiple deletions
      setTimeout(() => {
        setIsRemoving(false)
      }, 500)
    },
    [form, imageRefs, path, isRemoving],
  )

  const openFileDialog = () => {
    fileInputRef.current?.click()
  }

  const displayError = showError && errorMessage
  const { i18n } = useTranslation()
  const { theme } = useTheme()
  const language = i18n?.language || 'en'

  return (
    <>
      <div className="container">
        {/* Image previews */}
        {images.length > 0 && (
          <div className="imageGrid">
            {isUploading && (
              <div className="uploadOverlay">
                <div className="overlayContent">
                  <div className="spinner"></div>
                  <p className="uploadText">
                    {language === 'en'
                      ? 'Uploading images… please wait'
                      : language === 'fr'
                        ? 'Téléchargement des images… veuillez patienter'
                        : 'جاري تحميل الصور... يرجى الانتظار'}
                  </p>
                </div>
              </div>
            )}

            {isRemoving && (
              <div className="uploadOverlay">
                <div className="overlayContent">
                  <div className="spinner"></div>
                  <p className="uploadText">
                    {language === 'en'
                      ? 'Removing image… please wait'
                      : language === 'fr'
                        ? "Suppression de l'image… veuillez patienter"
                        : 'جاري حذف الصورة... يرجى الانتظار'}
                  </p>
                </div>
              </div>
            )}

            {images.map((image, index) => (
              <div key={image.id} className="imageItem">
                <div className="imagePreview">
                  <Image
                    src={image.url!}
                    alt={image.alt || `Image ${index + 1}`}
                    className="image"
                    fill
                  />
                </div>

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => removeImage(image.id)}
                  className="removeBtn"
                  disabled={isUploading || isRemoving}
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="header">
          {/* Main upload area */}
          <div
            className={`uploadArea ${isDragOver ? 'dragOver' : ''} ${displayError ? 'hasError' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              backgroundColor: theme === 'dark' ? '#000' : '#fff',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/jpg"
              onChange={handleFileSelect}
              className="fileInput"
              disabled={isUploading}
            />

            <div className="uploadContent">
              <div
                className="uploadIcon"
                style={{
                  backgroundColor: theme === 'dark' ? '#000' : '#fff',
                }}
              >
                <Upload size={24} />
              </div>

              <p className="mainText">
                {language === 'en'
                  ? 'Drag and drop or click to upload'
                  : language === 'fr'
                    ? 'Glisser et lacher ou cliquez pour uploader'
                    : 'اسحب وأسقط أو انقر لتحميل'}
              </p>

              <p className="subText">
                {language === 'en'
                  ? 'Photos must be JPEG or PNG format'
                  : language === 'fr'
                    ? 'Les photos doivent etre au format JPEG ou PNG'
                    : 'يجب ان تكون الصور بتنسيق JPEG او PNG'}
              </p>

              <button
                type="button"
                onClick={openFileDialog}
                className="browseBtn"
                style={{
                  backgroundColor: theme === 'dark' ? '#000' : '#fff',
                  color: theme === 'dark' ? '#fff' : '#000',
                }}
              >
                {language === 'en'
                  ? 'Browse Images'
                  : language === 'fr'
                    ? 'Parcourir les images'
                    : 'تصفح الصور'}{' '}
                <Upload size={16} />
              </button>
            </div>
          </div>

          {/* Error message */}
          {displayError && (
            <div className="errorMessage">
              <p>{displayError}</p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
